import React, { useState, useEffect } from 'react';
import { FiSearch, FiArrowLeft, FiRefreshCw, FiAlertCircle, FiClock, FiArrowRight } from 'react-icons/fi';
import { FaTrain } from 'react-icons/fa';

import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../components/Common/Card';
import Button from '../components/Common/Button';
import Shimmer from '../components/Common/Shimmer';
import apiService from '../services/api';
import { SearchTrainResult } from '../types';
import styles from './SearchTrainsResultPage.module.css';

const SearchTrainsResultPage: React.FC = () => {
  const { from, to } = useParams<{ from: string; to: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [trains, setTrains] = useState<SearchTrainResult[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (from && to) {
      fetchTrains(from, to);
    } else {
      navigate('/search-trains');
    }
  }, [from, to]);

  const fetchTrains = async (fromStn: string, toStn: string) => {
    setIsLoading(true);
    setError(null);
    setTrains([]);

    try {
      const response = await apiService.searchTrains(fromStn, toStn);

      if (response.success && response.data) {
        const trainsList = Array.isArray(response.data.data) ? response.data.data : [];
        
        if (trainsList.length > 0) {
          setTrains(trainsList);
          toast.success(`Found ${trainsList.length} trains!`);
        } else {
          setError('No trains found between these stations');
          toast.error('No trains found');
        }
      } else {
        setError(response.error || 'Failed to search trains');
        toast.error(response.error || 'Failed to search trains');
      }
    } catch (error) {
      setError('An error occurred. Please try again.');
      toast.error('An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = () => {
    if (from && to) {
      fetchTrains(from, to);
    }
  };

  const handleNewSearch = () => {
    navigate('/search-trains');
  };

  const formatRunningDays = (days: string) => {
    const daysMap = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    return (
      <div className={styles.runningDays}>
        {days.split('').map((day, index) => (
          <span
            key={index}
            className={`${styles.dayBadge} ${day === '1' ? styles.active : ''}`}
          >
            {daysMap[index]}
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className={styles.searchTrainsResultPage}>
      <div className="container">
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FiSearch className={styles.titleIcon} />
              Search Results
            </h1>
            <div className={styles.headerActions}>
              <Button variant="outline" size="md" onClick={handleNewSearch}>
                <FiArrowLeft />
                New Search
              </Button>
              <Button variant="secondary" size="md" onClick={handleRefresh} isLoading={isLoading}>
                <FiRefreshCw />
                Refresh
              </Button>
            </div>
          </div>
          <p className={styles.subtitle}>
            {from} <FiArrowRight className={styles.arrowIcon} /> {to}
          </p>
        </div>

        {/* Loading */}
        {isLoading && (
          <div className={styles.resultsSection}>
            <Shimmer type="card" count={3} />
          </div>
        )}

        {/* Error */}
        {!isLoading && error && (
          <Card className={styles.errorCard}>
            <div className={styles.errorContent}>
              <FiAlertCircle className={styles.errorIcon} />
              <h3 className={styles.errorTitle}>No Trains Found</h3>
              <p className={styles.errorMessage}>{error}</p>
              <div className={styles.errorActions}>
                <Button variant="primary" onClick={handleRefresh}>
                  Try Again
                </Button>
                <Button variant="outline" onClick={handleNewSearch}>
                  New Search
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Results */}
        {!isLoading && !error && trains.length > 0 && (
          <div className={styles.resultsSection}>
            <div className={styles.resultsHeader}>
              <h3 className={styles.resultsTitle}>
                Found {trains.length} train{trains.length > 1 ? 's' : ''}
              </h3>
            </div>

            <div className={styles.trainsList}>
              {trains.map((train, index) => (
                <Card key={index} hover className={styles.trainCard}>
                  <div className={styles.trainHeader}>
                    <div className={styles.trainNumber}>
                      <FaTrain />
                      {train.train_no}
                    </div>
                    <div className={styles.trainName}>{train.train_name}</div>
                  </div>

                  <div className={styles.trainRoute}>
                    <div className={styles.routePoint}>
                      <div className={styles.routeStation}>{train.from_stn_code}</div>
                      <div className={styles.routeTime}>{train.from_time}</div>
                      <div className={styles.routeStationName}>{train.from_stn_name}</div>
                    </div>

                    <div className={styles.routeDuration}>
                      <div className={styles.durationLine}></div>
                      <div className={styles.durationText}>
                        <FiClock />
                        {train.travel_time}
                      </div>
                      <div className={styles.durationLine}></div>
                    </div>

                    <div className={styles.routePoint}>
                      <div className={styles.routeStation}>{train.to_stn_code}</div>
                      <div className={styles.routeTime}>{train.to_time}</div>
                      <div className={styles.routeStationName}>{train.to_stn_name}</div>
                    </div>
                  </div>

                  <div className={styles.trainMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Distance:</span>
                      <span className={styles.metaValue}>{train.distance} km</span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>Halts:</span>
                      <span className={styles.metaValue}>{train.halts}</span>
                    </div>
                  </div>

                  <div className={styles.runningDaysSection}>
                    <span className={styles.runningDaysLabel}>Running Days:</span>
                    {formatRunningDays(train.running_days)}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchTrainsResultPage;